import { useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { Building2, FolderKanban, Users, LogOut, ShieldCheck, Cloud, Menu, X } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/lib/utils'

const nav = [
  { to: '/projects', label: 'Projetos', icon: FolderKanban },
  { to: '/tenants', label: 'Tenants', icon: Building2 },
  { to: '/onedrive-tool', label: 'OneDrive Tool', icon: Cloud },
  { to: '/users', label: 'Operadores', icon: Users },
]

export function Layout() {
  const { user, logout } = useAuth()
  // Menu escondido por padrão no mobile; abre como drawer off-canvas. No desktop (md+) é sidebar fixa.
  const [open, setOpen] = useState(false)

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Backdrop — só no mobile, quando o drawer está aberto */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar: drawer off-canvas no mobile, fixa no desktop */}
      <aside
        className={cn(
          'w-64 max-w-[80%] bg-slate-900 text-slate-200 flex flex-col z-50',
          'fixed inset-y-0 left-0 transition-transform duration-200 ease-in-out',
          'md:static md:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        )}
      >
        <div className="px-6 py-5 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded bg-brand-500 flex items-center justify-center font-bold text-white">M</div>
            <div>
              <div className="font-semibold text-white">M365 Transfer</div>
              <div className="text-xs text-slate-400">HAHOST</div>
            </div>
          </div>
          {/* Fechar — só no mobile */}
          <button
            onClick={() => setOpen(false)}
            className="md:hidden text-slate-400 hover:text-white"
            aria-label="Fechar menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-brand-600 text-white'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                )
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User box */}
        <div className="border-t border-slate-800 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-full bg-brand-500 flex items-center justify-center text-white font-medium">
              {user?.displayName?.charAt(0).toUpperCase() || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white truncate">{user?.displayName}</div>
              <div className="text-xs text-slate-400 flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" /> {user?.role}
              </div>
            </div>
          </div>
          <button
            onClick={() => logout()}
            className="w-full flex items-center justify-center gap-2 text-sm text-slate-300 hover:text-white py-2 rounded-md hover:bg-slate-800 transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sair
          </button>
        </div>
      </aside>

      {/* Coluna direita: topbar (mobile) + conteúdo. min-w-0 evita que filhos largos (tabelas) forcem overflow. */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar — só no mobile: hambúrguer + título */}
        <header className="md:hidden flex items-center gap-3 bg-slate-900 text-white px-4 py-3">
          <button onClick={() => setOpen(true)} aria-label="Abrir menu">
            <Menu className="h-6 w-6" />
          </button>
          <span className="font-semibold">M365 Transfer</span>
        </header>

        <main className="flex-1 overflow-auto">
          <div className="max-w-7xl mx-auto p-4 md:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
